/**
 * French & Jupps — Stanstead Abbotts, Hertfordshire, malting since 1689.
 *
 * A roasted-malt house: crystal and patent malts only, drum-roasted in small
 * batches from mostly local barley. They sell no base malt apart from a
 * Munich that has a tile on their range page but no product page, and it is
 * not here.
 *
 * Every product page prints the malt three times — an IoB tab, an EBC tab and
 * an ASBC tab — and, like Fawcett, the IoB colour band runs below the EBC one
 * on every page. The Fawcett rule in `uk.ts` applies: the stored colour is the
 * mid-point of the ASBC °Lovibond band, and EBC is derived with
 * `EBC = 2.65 × °L − 1.2`, so a row's EBC matches neither of their EBC
 * columns exactly. Black Malt and Roasted Barley publish only a minimum (443 °L
 * min), so that floor is stored and the real sack runs darker. Extract is the
 * IoB litre-degrees per kilogram ÷ 3.86, the same conversion the other British
 * rows use, which is why it sits a couple of points under the round
 * percentage their EBC tab prints. Moisture is the published maximum.
 *
 * Every malt they sell is a crystal or a roast, so diastatic power is zero by
 * process and every stored figure is theirs: all rows are `spec-sheet`.
 * Amber is typed `kilned-toasted` (the Joe White precedent) and Brown `roasted`.
 *
 * They publish no per-malt inclusion rate. The range page says 3–10 % for the
 * crystals, so no crystal ceiling here goes above 10, except the two pale cara
 * malts. The roast ceilings are ours.
 *
 * Crystal names follow the maker ("Light Crystal Malt") with the British EBC
 * grade they subtitle it with ("Crystal 100") kept out of the name and in the
 * tags, so the named-colour check does not read an IoB grade as °L.
 *
 * @see https://www.frenchandjupps.com/our-roasted-malts
 */

import type { Fermentable } from '../../schema/fermentable';

/** French & Jupps malts. @see Fermentable */
export const frenchAndJuppsFermentables: readonly Fermentable[] = [
  {
    id: 'french-jupps-cara-gold',
    name: 'Cara Gold',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71.2,
    moisturePercent: 7.5,
    colorLovibond: 5,
    colorEbc: 12.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Their palest crystal, developed with Campden BRI for premium lagers and low-alcohol beers. It gives a golden hue and smooth malt sweetness with honey and a touch of muscovado, at 4–6 °L.',
    tags: ['crystal', 'cara', 'lager', 'low-alcohol', 'british'],
    url: 'https://www.frenchandjupps.com/roasted-malts/cara-gold-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-cara-malt',
    name: 'Cara Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68.7,
    moisturePercent: 7.5,
    colorLovibond: 12.5,
    colorEbc: 31.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A classic low-color crystal at 10–15 °L with deep caramel sweetness and a rye-bread backbone. It is for distinctive lagers, lighter ales and lower-alcohol beers.',
    tags: ['crystal', 'cara', 'caramel', 'british'],
    url: 'https://www.frenchandjupps.com/roasted-malts/cara-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-light-crystal',
    name: 'Light Crystal Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 4,
    colorLovibond: 38.5,
    colorEbc: 100.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Crystal 100. It gives muscovado, caramel and honey at 34–43 °L with a biscuity, bread-crust finish, and adds golden color to milds, bitters and ales.',
    tags: ['crystal', 'caramel', 'british', 'crystal-100'],
    url: 'https://www.frenchandjupps.com/roasted-malts/light-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-medium-crystal',
    name: 'Medium Crystal Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 4,
    colorLovibond: 51.5,
    colorEbc: 135.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Crystal 120, at 47–56 °L. Caramel and muscovado with a raisin fruitiness and a rye-bread note, for bitters, ales and stouts.',
    tags: ['crystal', 'caramel', 'british', 'crystal-120'],
    url: 'https://www.frenchandjupps.com/roasted-malts/medium-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-standard-crystal',
    name: 'Standard Crystal Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 3.5,
    colorLovibond: 64.5,
    colorEbc: 169.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Crystal 150, at 60–69 °L. Muscovado and caramel with dried fruit and a light coffee finish. The everyday crystal for best bitters, amber ales and porters.',
    tags: ['crystal', 'caramel', 'british', 'crystal-150'],
    url: 'https://www.frenchandjupps.com/roasted-malts/standard-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-dark-crystal',
    name: 'Dark Crystal Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68.7,
    moisturePercent: 2.5,
    colorLovibond: 94.5,
    colorEbc: 249.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Crystal 220, at 85–104 °L. Black treacle, maple and prune over a biscuity base, with a little roast coffee at the end. It suits strong ales, porters and brown ales.',
    tags: ['crystal', 'dark-crystal', 'british', 'crystal-220', 'dried-fruit'],
    url: 'https://www.frenchandjupps.com/roasted-malts/dark-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-extra-dark-crystal',
    name: 'Extra Dark Crystal Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68.7,
    moisturePercent: 2.5,
    colorLovibond: 182.5,
    colorEbc: 482.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Crystal 400. It is stewed longer to 161–204 °L, giving muscovado, black treacle and roast coffee with a firm bitterness. A few percent is enough in a London porter, stout or red ale.',
    tags: ['crystal', 'dark-crystal', 'british', 'crystal-400', 'treacle'],
    url: 'https://www.frenchandjupps.com/roasted-malts/extra-dark-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-amber',
    name: 'Amber Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 68.7,
    moisturePercent: 2.5,
    colorLovibond: 13.5,
    colorEbc: 34.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A traditional English amber roasted from kilned malt to 9–18 °L. It gives breakfast toast and bread crust with a hint of coffee, and is the backbone of an 18th-century porter or a nutty amber ale.',
    tags: ['kilned', 'amber', 'british', 'toast', 'porter'],
    url: 'https://www.frenchandjupps.com/roasted-malts/amber-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-brown',
    name: 'Brown Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.7,
    moisturePercent: 2.5,
    colorLovibond: 47.5,
    colorEbc: 124.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A mahogany roast at 43–52 °L with coffee, a little smoke, roasted nuts and dried fruit. It gives dry depth to brown ales, porters and stouts.',
    tags: ['roasted', 'brown', 'british', 'coffee', 'porter'],
    url: 'https://www.frenchandjupps.com/roasted-malts/brown-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-light-chocolate',
    name: 'Light Chocolate Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 69.9,
    moisturePercent: 2.5,
    colorLovibond: 74.5,
    colorEbc: 196.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'One of the palest chocolate malts sold, at 67–82 °L. It leans toward milk chocolate and muscovado sweetness rather than roast, with a gentle smoke. It suits brown ales, porters and softer stouts.',
    tags: ['roasted', 'chocolate', 'british', 'milk-chocolate'],
    url: 'https://www.frenchandjupps.com/roasted-malts/light-chocolate-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-pale-chocolate',
    name: 'Pale Chocolate Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 69.9,
    moisturePercent: 2.5,
    colorLovibond: 185,
    colorEbc: 489.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Between their light and classic chocolate at 148–222 °L. Roast coffee and toast with cocoa and a pleasant bitterness, for stouts, milk stouts and porters.',
    tags: ['roasted', 'chocolate', 'british', 'coffee'],
    url: 'https://www.frenchandjupps.com/roasted-malts/pale-chocolate-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-chocolate',
    name: 'Chocolate Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.7,
    moisturePercent: 2.5,
    colorLovibond: 370,
    colorEbc: 979.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 7,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Their classic chocolate at 333–407 °L. It is assertive, with espresso, dark chocolate and a smoky bitterness. The dark-beer workhorse for stouts, porters and dark bocks.',
    tags: ['roasted', 'chocolate', 'british', 'espresso', 'stout'],
    url: 'https://www.frenchandjupps.com/roasted-malts/chocolate-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-black',
    name: 'Black Malt',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.7,
    moisturePercent: 3.5,
    colorLovibond: 443,
    colorEbc: 1172.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 3,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Patent black malt roasted for maximum color, published only as a 443 °L minimum. It is smooth but assertive, with espresso and a barbecue-coal smoke. Use it for color and dryness in porters, stouts and schwarzbier.',
    tags: ['roasted', 'black', 'british', 'colour-malt', 'espresso'],
    url: 'https://www.frenchandjupps.com/roasted-malts/black-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'french-jupps-roasted-barley',
    name: 'Roasted Barley',
    maker: 'French & Jupps',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 67.4,
    moisturePercent: 2.5,
    colorLovibond: 443,
    colorEbc: 1172.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted local winter barley roasted near-black, published as a 443 °L minimum. It has strong espresso and dry bitterness. A touch gives a red ale its hue, and a few percent defines an Irish stout.',
    tags: ['roasted', 'unmalted', 'british', 'stout', 'coffee'],
    url: 'https://www.frenchandjupps.com/roasted-malts/roasted-barley/',
    provenance: 'spec-sheet',
  },
];
